// The Gemini models an organisation can pick in Settings.
//
// The dropdown reads Google's live list through the claims-ai-models edge
// function, so a new release appears without a code change. The static list
// below is only the fallback shown when that call fails. The server keeps its
// own copy in supabase/functions/_shared/claims-gemini-models.ts (Deno cannot
// import from src), and the drift test in
// supabase/functions/_shared/claims-gemini-models.test.ts fails if the two
// lists stop matching. Names are the ones the Claims Gemini key can call.

// What the app falls back to when "auto" cannot be resolved.
export const CLAIMS_DEFAULT_AI_MODEL = "gemini-3.8-flash";

// The saved value that means "the newest stable Flash". The server resolves it.
export const CLAIMS_AUTO_AI_MODEL = "auto";

// Every selectable model in the fallback list, newest first.
export const CLAIMS_SELECTABLE_AI_MODELS: readonly string[] = [
  "gemini-3.8-flash",
  "gemini-3.6-flash",
  "gemini-3.1-pro-preview",
  "gemini-3.1-flash-lite-preview",
  "gemini-3-flash-preview",
  "gemini-2.5-pro",
  "gemini-2.5-flash",
  "gemini-2.5-flash-lite",
];

export type ClaimsAiFamily = "flash" | "flash-lite" | "pro";

export interface ClaimsAiModelOption {
  id: string;
  label: string;
  family: ClaimsAiFamily;
  preview: boolean;
}

export interface ClaimsAiModelList {
  models: ClaimsAiModelOption[];
  auto: string;
  source: "live" | "fallback";
}

const FAMILY_ORDER: readonly ClaimsAiFamily[] = ["flash", "flash-lite", "pro"];

export const CLAIMS_AI_FAMILY_LABELS: Record<ClaimsAiFamily, string> = {
  flash: "Flash",
  "flash-lite": "Flash-Lite",
  pro: "Pro",
};

const MODEL_NAME = /^gemini-(\d+(?:\.\d+)?)-(flash-lite|flash|pro)(-preview)?$/;

export function parseClaimsAiModel(id: string): ClaimsAiModelOption | null {
  const m = MODEL_NAME.exec(id);
  if (!m) return null;
  const family = m[2] as ClaimsAiFamily;
  return {
    id,
    label: `Gemini ${m[1]} ${CLAIMS_AI_FAMILY_LABELS[family]}`,
    family,
    preview: m[3] !== undefined,
  };
}

function versionOf(id: string): [number, number] {
  const [major, minor] = (MODEL_NAME.exec(id)?.[1] ?? "0").split(".");
  return [Number(major), minor === undefined ? 0 : Number(minor)];
}

// The list shown when Google's list cannot be loaded.
export function claimsAiFallbackList(): ClaimsAiModelList {
  const models = CLAIMS_SELECTABLE_AI_MODELS.map(parseClaimsAiModel)
    .filter((m): m is ClaimsAiModelOption => m !== null)
    .sort((a, b) => {
      const [aMaj, aMin] = versionOf(a.id);
      const [bMaj, bMin] = versionOf(b.id);
      if (aMaj !== bMaj) return bMaj - aMaj;
      if (aMin !== bMin) return bMin - aMin;
      if (a.preview !== b.preview) return a.preview ? 1 : -1;
      return FAMILY_ORDER.indexOf(a.family) - FAMILY_ORDER.indexOf(b.family);
    });
  const auto = models.find((m) => m.family === "flash" && !m.preview)?.id ??
    CLAIMS_DEFAULT_AI_MODEL;
  return { models, auto, source: "fallback" };
}

// Read the edge function's answer. Anything that is not the documented shape
// becomes the fallback list with failed = true, so the page never shows junk.
export function readClaimsAiModelList(
  data: unknown,
): { list: ClaimsAiModelList; failed: boolean } {
  const d = data as Partial<ClaimsAiModelList> | null;
  if (d && Array.isArray(d.models) && typeof d.auto === "string") {
    const models = d.models.filter(
      (m): m is ClaimsAiModelOption =>
        !!m && typeof m.id === "string" && typeof m.label === "string" &&
        FAMILY_ORDER.includes(m.family as ClaimsAiFamily) &&
        typeof m.preview === "boolean",
    );
    if (models.length > 0) {
      return {
        list: {
          models,
          auto: d.auto,
          source: d.source === "live" ? "live" : "fallback",
        },
        failed: false,
      };
    }
  }
  return { list: claimsAiFallbackList(), failed: true };
}

export interface ClaimsAiModelGroup {
  family: ClaimsAiFamily;
  label: string;
  models: ClaimsAiModelOption[];
}

// Flash, Flash-Lite, Pro, each newest first as the server sent them. Empty
// groups are dropped.
export function groupClaimsAiModels(
  models: readonly ClaimsAiModelOption[],
): ClaimsAiModelGroup[] {
  return FAMILY_ORDER.map((family) => ({
    family,
    label: CLAIMS_AI_FAMILY_LABELS[family],
    models: models.filter((m) => m.family === family),
  })).filter((g) => g.models.length > 0);
}

export function claimsAiOptionLabel(m: ClaimsAiModelOption): string {
  return m.preview ? `${m.label} (Preview)` : m.label;
}

export function claimsAiAutoLabel(autoId: string): string {
  return `Auto (newest Flash: ${autoId})`;
}

// The one-line note under the Settings dropdown, by family. A model that is not
// on the list says so instead of borrowing another model's note.
export function claimsAiModelHint(model: string): string {
  if (model === CLAIMS_AUTO_AI_MODEL) {
    return "Uses the newest stable Flash model from Google. It updates itself when Google releases a new one.";
  }
  const info = parseClaimsAiModel(model);
  if (!info) return "This model is not on the list. Choose one above.";
  const base = info.family === "flash"
    ? "Fast and balanced. Suits most claims work."
    : info.family === "flash-lite"
    ? "Fastest and cheapest. Suits simple, high-volume tasks."
    : "Most accurate for complex analysis. Slower and costs more.";
  return info.preview
    ? `${base} Preview models can change or be withdrawn without notice.`
    : base;
}
